import type { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { WidthProps } from 'styled-system';
import {
  border,
  borderStyle,
  borderWidth,
  maxWidth,
  minWidth,
  borderColor as styledBorderColor,
  themeGet,
  width,
} from 'styled-system';

import { Text } from '../Text';

export enum TooltipPosition {
  Top = 'top',
  Left = 'left',
  Right = 'right',
  Bottom = 'bottom',
}

interface TooltipProps extends WidthProps, PropsWithChildren {
  show: boolean;
  eventListenersDisabled: boolean;
  head: string | ReactNode | ReactNode[];
  position: string | ReactNode | ReactNode[];
  className?: string;
  ariaLabel?: string;
  headAriaLabel?: string;
}

interface TooltipState {
  show: boolean;
}

const UnstyledTooltipText = styled(Text)<any>`
  ${({ position }) => {
    switch (position) {
      case TooltipPosition.Left:
        return css`
          top: 50%;
          right: 100%;
          margin-right: 10px;
          transform: translate(0, -50%);
        `;
      case TooltipPosition.Right:
        return css`
          top: 50%;
          left: 100%;
          margin-left: 10px;
          transform: translate(0, -50%);
        `;
      case TooltipPosition.Bottom:
        return css`
          top: 40px;
          left: 50%;
          transform: translate(-50%, 0);
        `;
      case TooltipPosition.Top:
        return css`
          top: -20px;
          left: 50%;
          transform: translate(-50%, -100%);
        `;
      default:
        return '';
    }
  }};

  ${width};
  ${minWidth};
  ${maxWidth};
  ${borderStyle};
  ${borderWidth};
  ${styledBorderColor};
  ${border};
  background-color: ${themeGet('colors.sys.neutral.background.default')};
  border-radius: 2px;
  position: absolute;
  z-index: 500;
  box-sizing: border-box;
  box-shadow: 0 0 6px 0 rgba(44, 54, 56, 0.25);

  &::after,
  &::before {
    border: solid transparent;
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    ${({ position }) => {
      switch (position) {
        case TooltipPosition.Left:
          return css`
            left: 100%;
            top: 50%;
          `;
        case TooltipPosition.Right:
          return css`
            right: 100%;
            top: 50%;
          `;
        case TooltipPosition.Bottom:
          return css`
            bottom: 100%;
            left: 50%;
          `;
        case TooltipPosition.Top:
          return css`
            top: 100%;
            left: 50%;
          `;
        default:
          return '';
      }
    }};
  }

  &::after {
    border-width: 8px;
    ${({ theme, position }) => {
      const borderColor = theme.colors.sys.neutral.background.default;
      switch (position) {
        case TooltipPosition.Left:
          return css`
            border-left-color: ${borderColor};
            margin-top: -8px;
          `;
        case TooltipPosition.Right:
          return css`
            border-right-color: ${borderColor};
            margin-top: -8px;
          `;
        case TooltipPosition.Bottom:
          return css`
            border-bottom-color: ${borderColor};
            margin-left: -8px;
          `;
        case TooltipPosition.Top:
          return css`
            border-top-color: ${borderColor};
            margin-left: -8px;
          `;
        default:
          return '';
      }
    }};
  }

  &::before {
    border-width: 9px;
    ${({ theme, position }) => {
      const borderColor = theme.colors.sys.neutral.background.active;
      switch (position) {
        case TooltipPosition.Left:
          return css`
            border-left-color: ${borderColor};
            margin-top: -9px;
          `;
        case TooltipPosition.Right:
          return css`
            border-right-color: ${borderColor};
            margin-top: -9px;
          `;
        case TooltipPosition.Bottom:
          return css`
            border-bottom-color: ${borderColor};
            margin-left: -9px;
          `;
        case TooltipPosition.Top:
          return css`
            border-top-color: ${borderColor};
            margin-left: -9px;
          `;
        default:
          return '';
      }
    }};
  }
`;

const TooltipText = (props: ComponentProps<typeof UnstyledTooltipText>) => (
  <UnstyledTooltipText
    p={3}
    lineHeight={1}
    borderStyle="solid"
    borderWidth="small"
    borderColor="sys.neutral.border.weak"
    width={[null, 'max-content']}
    minWidth={['100%', '0%']}
    maxWidth={[null, '30vw']}
    {...props}
  />
);

const Wrapper = styled.div<any>`
  position: relative;
`;

export default class Tooltip extends React.Component<TooltipProps, TooltipState> {
  static defaultProps = {
    show: false,
    eventListenersDisabled: false,
    position: TooltipPosition.Top,
  };

  node: HTMLElement;

  constructor(props: TooltipProps) {
    super(props);
    this.state = {
      show: props.show,
    };
    this.handleShow = this.handleShow.bind(this);
    this.handleHide = this.handleHide.bind(this);
    this.handleTouch = this.handleTouch.bind(this);
  }
  componentDidUpdate(prevProps: TooltipProps) {
    const { show } = this.props;

    if (show !== prevProps.show) {
      this.setState({
        show,
      });
    }
  }
  componentDidMount() {
    if (
      !this.props.eventListenersDisabled &&
      this.node &&
      typeof this.node.addEventListener === 'function'
    ) {
      this.node.addEventListener('mouseenter', this.handleShow, false);
      this.node.addEventListener('mouseleave', this.handleHide, false);
      this.node.addEventListener('touchstart', this.handleTouch, false);
    }
  }
  componentWillUnmount() {
    if (
      !this.props.eventListenersDisabled &&
      this.node &&
      typeof this.node.removeEventListener === 'function'
    ) {
      this.node.removeEventListener('mouseenter', this.handleShow, false);
      this.node.removeEventListener('mouseleave', this.handleHide, false);
      this.node.removeEventListener('touchstart', this.handleTouch, false);
    }
  }

  handleShow() {
    this.setState({
      show: true,
    });
  }

  handleHide() {
    this.setState({
      show: false,
    });
  }

  handleTouch() {
    this.handleShow();
    this.handleOutsideClick();
  }

  handleOutsideClick() {
    const fn = event => {
      if (!this.node.contains(event.target)) {
        this.handleHide();
        document.removeEventListener('touchstart', fn, false);
      }
    };
    document.addEventListener('touchstart', fn, false);
  }

  render() {
    return (
      <Wrapper
        ref={node => {
          this.node = node;
        }}
        data-testid="tooltip"
        className={this.props.className}
      >
        <span aria-label={(!this.state.show && this.props.headAriaLabel) || ''}>
          {this.props.head}
        </span>
        {this.state.show && (
          <TooltipText
            role="tooltip"
            data-testid="tooltip-content"
            position={this.props.position}
            width={this.props.width}
            aria-label={this.props.ariaLabel || ''}
          >
            {this.props.children}
          </TooltipText>
        )}
      </Wrapper>
    );
  }
}
